/**
 * FormFieldSkeleton Component
 * Loading placeholder for form input fields
 * Reusable for text inputs, selects, textareas, etc.
 */

interface FormFieldSkeletonProps {
  /** Type of field to display skeleton for */
  fieldType?: 'input' | 'select' | 'textarea';
  /** Whether to show a label skeleton above the field */
  showLabel?: boolean;
  /** Custom label width (as Tailwind width class, e.g., 'w-32') */
  labelWidth?: string;
}

export function FormFieldSkeleton({
  fieldType = 'input',
  showLabel = true,
  labelWidth = 'w-32',
}: FormFieldSkeletonProps) {
  return (
    <div className="space-y-2" aria-label="Loading form field" role="status">
      {/* Label skeleton */}
      {showLabel && (
        <div className={`h-4 ${labelWidth} bg-gray-200 rounded animate-pulse`}></div>
      )}

      {/* Field skeleton - varies by type */}
      {fieldType === 'input' && (
        <div className="h-11 w-full bg-gray-200 rounded-md border border-gray-300 animate-pulse"></div>
      )}

      {fieldType === 'select' && (
        <div className="h-11 w-full bg-gray-200 rounded-md border border-gray-300 animate-pulse relative">
          {/* Dropdown indicator */}
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
        </div>
      )}

      {fieldType === 'textarea' && (
        <div className="h-24 w-full bg-gray-200 rounded-md border border-gray-300 animate-pulse"></div>
      )}
    </div>
  );
}

/**
 * FormSectionSkeleton Component
 * Loading placeholder for a group of form fields with a heading
 */
interface FormSectionSkeletonProps {
  /** Number of fields to show in this section */
  fieldCount?: number;
  /** Whether to show a section heading skeleton */
  showHeading?: boolean;
}

export function FormSectionSkeleton({ fieldCount = 3, showHeading = true }: FormSectionSkeletonProps) {
  return (
    <div className="space-y-6" aria-label="Loading form section" role="status">
      {/* Section heading skeleton */}
      {showHeading && (
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
      )}

      {/* Field skeletons */}
      <div className="space-y-4">
        {Array.from({ length: fieldCount }).map((_, index) => (
          <FormFieldSkeleton
            key={index}
            fieldType={index % 3 === 0 ? 'select' : 'input'}
          />
        ))}
      </div>
    </div>
  );
}
